import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Calendar, Eye, EyeOff, ShieldCheck, Target, Unlock } from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { AutofillUsername } from "../components/AutofillUsername";
import { photoBackground } from "../components/Cards";
import { KuspitNotice } from "../components/Disclosures";
import { PhotoPicker } from "../components/PhotoPicker";
import { usePageHeader } from "../components/shell/PageHeaderContext";
import { Spinner } from "../components/Spinner";
import { apiRequest, describeApiError } from "../lib/api";
import { formatCalendarDate, money, moneyWhole, monthsUntil, savingsPlan, todayIso } from "../lib/format";
import { uploadPhoto, useDiscover, useDreamPhotos, useKyc, useLibreWallet, useProducts } from "../lib/queries";
import type { ApiEnvelope, MediaPhoto, Wallet } from "../types";
import { ButtonLabel } from "../components/ButtonLabel";

const PREVIEW_SHADE = "linear-gradient(180deg, rgba(0,37,87,0.12) 30%, rgba(0,37,87,0.9) 100%)";

/**
 * "Nueva Wallet Libre · Tu sueño" — two steps: define the dream (title, target, date, photo) with a
 * live preview, then confirm and open the wallet. Opening is a financial action, so the second step
 * confirms the password (step-up) before the wallet is created.
 */
export function CreateDreamPage() {
  const { user, applyUser } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { wallet, isLoading } = useLibreWallet();
  const kyc = useKyc();
  const products = useProducts();
  const photos = useDreamPhotos();
  const regions = useDiscover();
  // Drives the savings projection only. It is deliberately never rendered: the platform does not
  // advertise a rate, and a figure on screen reads as a promised yield however it is worded.
  const rate = products.data?.settings.wallet_libre?.annualRatePercent ?? 11;

  const [step, setStep] = useState<1 | 2>(1);
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState("");
  const [photoId, setPhotoId] = useState<string | null>(null);
  const [photo, setPhoto] = useState<MediaPhoto | null>(null);
  const [prefilled, setPrefilled] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string[] | null>(null);

  usePageHeader(step === 1 ? "Nueva Wallet Libre" : "Confirma tu sueño", step === 1 ? "Paso 1 de 2 · define tu sueño" : "Paso 2 de 2 · revisa y crea tu wallet");

  // Arriving from a trip page ("Ahorrar para después") pre-fills the goal with that trip's estimate.
  const tripTitle = searchParams.get("titulo");
  useEffect(() => {
    if (prefilled || !tripTitle) return;
    setTitle(tripTitle);
    const meta = Number(searchParams.get("meta"));
    if (Number.isFinite(meta) && meta > 0) setAmount(String(Math.round(meta)));
    const fecha = searchParams.get("fecha");
    if (fecha && /^\d{4}-\d{2}-\d{2}$/.test(fecha)) setDate(fecha);
    setPrefilled(true);
  }, [tripTitle, searchParams, prefilled]);

  // Arriving from a destination card pre-fills the dream with that trip.
  const destinationSlug = searchParams.get("destino");
  useEffect(() => {
    if (prefilled || !destinationSlug || !regions.data) return;
    const destination = regions.data.flatMap((region) => region.destinations).find((item) => item.slug === destinationSlug);
    if (!destination) return;
    setTitle(`Viaje a ${destination.name}`);
    if (destination.price) setAmount(String(Math.round(destination.price)));
    if (destination.photoId && destination.photoUrl) {
      setPhotoId(destination.photoId);
      setPhoto({ id: destination.photoId, url: destination.photoUrl, kind: "destination", title: destination.name, mime: "", bytes: 0, width: null, height: null, sortOrder: 0, active: true, ownerId: null, createdAt: "" });
    }
    setPrefilled(true);
  }, [destinationSlug, regions.data, prefilled]);

  const goal = Number(amount);
  const months = monthsUntil(date);
  const plan = useMemo(() => savingsPlan(goal, months, rate), [goal, months, rate]);
  const selectedPhoto = photo ?? photos.data?.find((item) => item.id === photoId) ?? null;
  const needsPassword = user?.hasPassword === false;

  if (isLoading || kyc.isLoading) return <div className="page-state"><Spinner label="Cargando" /></div>;
  if (wallet) return <Navigate to="/wallet/libre" replace />;
  // Kuspit opens the contract with the customer's own data: collected first where it is required.
  if (kyc.data?.profileRequired && !kyc.data.profile) {
    return <Navigate to={`/verificacion/datos?siguiente=${encodeURIComponent(`/wallet/libre/nueva${window.location.search}`)}`} replace />;
  }

  const continueToConfirm = (event: FormEvent) => {
    event.preventDefault(); setError(null);
    if (title.trim().length < 2) { setError(["Ponle un nombre a tu viaje."]); return; }
    if (!Number.isFinite(goal) || goal <= 0) { setError(["Ingresa el monto que quieres ahorrar."]); return; }
    if (!date) { setError(["Elige la fecha objetivo."]); return; }
    setStep(2);
  };

  const create = async (event: FormEvent) => {
    event.preventDefault(); setError(null);
    if (!accepted) { setError(["Confirma que la información de tu perfil es correcta."]); return; }
    setSubmitting(true);
    try {
      if (needsPassword) {
        const created = await apiRequest<ApiEnvelope<{ user: NonNullable<typeof user> }>>("/api/v1/auth/password/set", { method: "POST", body: JSON.stringify({ password }) });
        applyUser(created.data.user);
      }
      const stepUp = await apiRequest<ApiEnvelope<{ stepUpToken: string }>>("/api/v1/auth/step-up", { method: "POST", body: JSON.stringify({ password }) });
      const result = await apiRequest<ApiEnvelope<{ wallet: Wallet }>>("/api/v1/wallets", {
        method: "POST",
        headers: { "idempotency-key": crypto.randomUUID(), "x-step-up-token": stepUp.data.stepUpToken },
        body: JSON.stringify({ product: "libre", goalTitle: title.trim(), goalAmount: goal, goalDate: date, goalPhotoId: photoId })
      });
      queryClient.setQueryData(["wallets"], { data: { wallets: [result.data.wallet] } });
      await queryClient.invalidateQueries({ queryKey: ["wallets"] });
      navigate("/wallet/libre", { replace: true });
    } catch (cause) {
      if ((cause as { code?: string }).code === "KYC_PROFILE_REQUIRED") {
        navigate(`/verificacion/datos?siguiente=${encodeURIComponent("/wallet/libre/nueva")}`);
        return;
      }
      setError(describeApiError(cause, "No pudimos abrir la wallet."));
    } finally { setSubmitting(false); }
  };

  const verified = user?.kycStatus === "verified";

  if (step === 2) {
    return (
      <div className="page-container">
        <div className="dream-grid confirm">
          <div>
            <section className="dream-preview large" style={photoBackground(selectedPhoto?.url, PREVIEW_SHADE, "half")}>
              <span className="hero-eyebrow">Wallet Libre</span>
              <div className="dream-preview-foot">
                <div>
                  <strong>{title}</strong>
                  <span translate="no"><Target size={15} aria-hidden="true" /> {moneyWhole.format(goal)} <i>·</i> <Calendar size={15} aria-hidden="true" /> {formatCalendarDate(date, "long")}</span>
                </div>
              </div>
            </section>
            <section className="card summary-card">
              {[
                ["Título del viaje", title],
                ["Monto a ahorrar", money.format(goal)],
                ["Fecha objetivo", formatCalendarDate(date, "long")],
                ["Plazo", `${months} ${months === 1 ? "mes" : "meses"}`],
                ["Aporte sugerido", `${money.format(plan.monthly)} / mes`],
                ["Comisión de apertura", "Sin comisión"]
              ].map(([label, value]) => (
                <div className="summary-row" key={label}><span>{label}</span><strong translate="no">{value}</strong></div>
              ))}
            </section>
          </div>
          <div>
            <section className="interest-card">
              <div className="interest-rate"><strong>Rendimiento</strong><span>variable<br />estimado</span></div>
              <div className="interest-split">
                <div><span>Ganarás aprox.</span><strong translate="no">+{moneyWhole.format(plan.interest)}</strong></div>
                <i />
                <div><span>Tú aportas</span><strong translate="no">{moneyWhole.format(goal - plan.interest)}</strong></div>
              </div>
            </section>
            <div className="unlock-note"><Unlock size={17} aria-hidden="true" /><span>Sin penalización: puedes retirar, mover la fecha o subir el monto en cualquier momento.</span></div>
            {verified ? (
              <form className="card confirm-form" onSubmit={create}>
                <AutofillUsername />
                <label htmlFor="create-password">{needsPassword ? "Crea una contraseña para confirmar" : "Confirma tu contraseña"}</label>
                <div className="password-field">
                  <input id="create-password" type={showPassword ? "text" : "password"} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete={needsPassword ? "new-password" : "current-password"} minLength={needsPassword ? 12 : undefined} required />
                  <button type="button" className="field-icon" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}>
                    {showPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
                  </button>
                </div>
                {needsPassword && <span className="field-hint">Tu cuenta se creó con Apple o Google. Abrir una wallet necesita una contraseña: 12 caracteres, mayúscula, minúscula, número y símbolo.</span>}
                <label className="check-row">
                  <input type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} />
                  <span>Confirmo que la información de mi perfil es correcta.</span>
                </label>
                {error && <div className="form-error" role="alert">{error.join(" ")}</div>}
                <button className="primary-button full" type="submit" disabled={submitting}><ButtonLabel busy={submitting} busyLabel="Abriendo tu wallet" label="Crear mi wallet" /></button>
              </form>
            ) : (
              <div className="card confirm-form">
                <div className="eligibility-row dark"><ShieldCheck size={17} aria-hidden="true" /><span>Identidad pendiente de verificación</span></div>
                <p className="field-hint">Podrás crear tu wallet en cuanto el equipo Aventuria verifique tu identidad.</p>
              </div>
            )}
            <button type="button" className="text-link center" onClick={() => setStep(1)}>Editar mi sueño</button>
            <p className="wallet-legal">Rendimiento variable y no garantizado; las cifras son una estimación, no una promesa de rendimiento.</p>
            <KuspitNotice />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="dream-grid">
        <form className="card dream-form" onSubmit={continueToConfirm}>
          <span className="page-eyebrow">Tu sueño</span>
          <h2>Ponle nombre a tu próximo viaje.</h2>
          <p>Tú decides el destino, el monto y la fecha, y puedes ajustarlos cuando quieras.</p>
          <div className="field-block">
            <label htmlFor="dream-title">Título del viaje</label>
            <input id="dream-title" className="standalone-input large" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Luna de miel en Maldivas" maxLength={120} autoFocus required />
          </div>
          <div className="field-grid">
            <div className="field-block">
              <label htmlFor="dream-amount">Monto a ahorrar</label>
              <div className="money-input">
                <span aria-hidden="true">$</span>
                <input id="dream-amount" inputMode="decimal" autoComplete="off" value={amount} onChange={(event) => setAmount(event.target.value.replace(/[^\d.]/g, ""))} placeholder="4,800" required />
                <span>MXN</span>
              </div>
              <span className="field-hint">Puedes cambiarlo después sin costo.</span>
            </div>
            <div className="field-block">
              <label htmlFor="dream-date">Fecha objetivo</label>
              <div className="auth-input-wrap">
                <Calendar size={17} aria-hidden="true" />
                <input id="dream-date" type="date" min={todayIso(30)} value={date} onChange={(event) => setDate(event.target.value)} required />
              </div>
              <span className="field-hint">{date ? `${months} ${months === 1 ? "mes" : "meses"} desde hoy · aporte sugerido ${money.format(plan.monthly)}/mes` : "Elige cuándo quieres viajar."}</span>
            </div>
          </div>
          <div className="field-block">
            <span className="field-label">Foto de tu sueño</span>
            <PhotoPicker
              photos={photos.data ?? []}
              value={photoId}
              onChange={(id, selected) => { setPhotoId(id); setPhoto(selected ?? null); }}
              onUpload={(file) => uploadPhoto(file, { title: title || "Mi sueño" })}
            />
          </div>
          {error && <div className="form-error" role="alert">{error.join(" ")}</div>}
          <div className="inline-actions">
            <button className="primary-button" type="submit">Continuar</button>
            <button className="text-link" type="button" onClick={() => navigate("/wallet")}><ArrowLeft size={14} aria-hidden="true" /> Cancelar</button>
          </div>
        </form>

        <aside className="dream-side">
          <span className="rail-title section-eyebrow">Vista previa de tu wallet</span>
          <section className="dream-preview" style={photoBackground(selectedPhoto?.url, PREVIEW_SHADE, "half")}>
            <span className="hero-eyebrow">Wallet Libre</span>
            <div className="dream-preview-foot">
              <div>
                <strong>{title || "Tu próximo viaje"}</strong>
                <span translate="no"><Target size={14} aria-hidden="true" /> {goal > 0 ? moneyWhole.format(goal) : "—"} <i>·</i> <Calendar size={14} aria-hidden="true" /> {date ? formatCalendarDate(date, "long") : "—"}</span>
              </div>
            </div>
          </section>
          <section className="card plan-card">
            <strong>Proyección estimada</strong>
            <div className="summary-row"><span>Aporte sugerido</span><strong translate="no">{goal > 0 && date ? `${money.format(plan.monthly)} / mes` : "—"}</strong></div>
            <div className="summary-row"><span>Plazo</span><strong>{date ? `${months} ${months === 1 ? "mes" : "meses"}` : "—"}</strong></div>
            <div className="summary-row"><span>Rendimiento estimado</span><strong className="hi" translate="no">{goal > 0 && date ? `+${moneyWhole.format(plan.interest)}` : "—"}</strong></div>
          </section>
        </aside>
      </div>
    </div>
  );
}
